import { useState, useEffect, useCallback } from 'react';
import { salesControllingApi } from '../services/salesControllingApi';
import type { SCFilterOptions, SCFilters } from '../types/salesControlling';

const STORAGE_KEY = 'sc-filters';

/** Get YTD default dates: Jan 1 to end of last complete month */
function getYtdDefaults(): { dateFrom: string; dateTo: string } {
    const now = new Date();
    const year = now.getFullYear();
    const lastCompleteMonthEnd = new Date(year, now.getMonth(), 0);
    const m = String(lastCompleteMonthEnd.getMonth() + 1).padStart(2, '0');
    const d = String(lastCompleteMonthEnd.getDate()).padStart(2, '0');
    return {
        dateFrom: `${year}-01-01`,
        dateTo: `${year}-${m}-${d}`,
    };
}

function loadSavedFilters(): Partial<SCFilters> {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return {};
}

function saveFilters(filters: SCFilters) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            company: filters.company,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
        }));
    } catch { /* ignore */ }
}

export function useSalesControlling() {
    const ytd = getYtdDefaults();
    const saved = loadSavedFilters();

    const [filterOptions, setFilterOptions] = useState<SCFilterOptions>({
        companies: [],
        dateRange: { min: ytd.dateFrom, max: ytd.dateTo },
        businessUnits: [],
        salesManagers: [],
    });
    const [filters, setFilters] = useState<SCFilters>({
        company: saved.company || '',
        dateFrom: saved.dateFrom || ytd.dateFrom,
        dateTo: saved.dateTo || ytd.dateTo,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        salesControllingApi.getFilters()
            .then(opts => {
                setFilterOptions(opts);
                // Only set default company if no saved and no current selection
                if (opts.companies.length > 0 && !filters.company) {
                    setFilters(prev => ({ ...prev, company: opts.companies[0].code }));
                }
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist filters to sessionStorage
    useEffect(() => {
        saveFilters(filters);
    }, [filters]);

    const updateFilters = useCallback((updates: Partial<SCFilters>) => {
        setFilters(prev => ({ ...prev, ...updates }));
    }, []);

    return {
        filterOptions,
        filters,
        updateFilters,
        loading,
        error,
    };
}
